import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';

import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import '../../Views/LandingPage/LandingPage.module.css';

const PortfolioNoticeModal = ({ open, onClose }) => {
  const { isDark, colors } = useThemeStyles();

  const handlePrimaryAction = () => {
    onClose();
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      keepMounted
      showCloseButton={false}
      width={520}
      hideBackdrop
      disableAutoFocus
      ariaLabelledby="portfolio-modal-title"
      ariaDescribedby="portfolio-modal-description"
      containerSx={{
        width: { xs: '90vw', sm: '520px' },
        maxWidth: '520px',
        borderRadius: '16px',
        px: { xs: 3, sm: 4 },
        pt: 3,
        pb: 4,
        border: 'none',
        animation: 'portfolioFadeInScale 220ms ease-out forwards',
      }}
      contentSx={{
        gap: 3,
      }}
    >
      <Box role="dialog" aria-modal="true">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2.5,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="portfolio-modal-title"
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: colors.text.primary,
                mb: 0.5,
              }}
            >
              PlotWyze – Portfolio Build
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                color: colors.text.secondary,
                lineHeight: 1.5,
              }}
            >
              Crafted by <strong style={{ color: colors.text.primary }}>Alexander Koszuta</strong>{' '}
              as part of a live portfolio.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close portfolio notice"
            onClick={onClose}
            sx={{
              ml: 1,
              color: colors.text.secondary,
              p: 1.25,
              '& .MuiSvgIcon-root': {
                fontSize: 24,
              },
              '&:hover': {
                color: colors.text.primary,
                backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)',
              },
              '&:focus-visible': {
                outline: '2px solid #667eea',
                outlineOffset: 2,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography
          id="portfolio-modal-description"
          sx={{
            mb: 3,
            color: colors.text.secondary,
            lineHeight: 1.7,
            fontSize: 14,
          }}
        >
          You’re exploring an in-progress version of PlotWyze. The app is fully safe to click around
          in, and parts of the experience may evolve as the product continues to grow.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* Primary CTA */}
          <Button
            type="button"
            variant="contained"
            fullWidth
            onClick={handlePrimaryAction}
            sx={{
              mt: 0.5,
              py: 1.25,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 15,
              backgroundColor: '#667eea',
              color: '#ffffff',
              boxShadow: '0 10px 25px rgba(102, 126, 234, 0.35)',
              '&:hover': {
                backgroundColor: '#5a6fdc',
                boxShadow: '0 12px 30px rgba(102, 126, 234, 0.45)',
              },
              '&:focus-visible': {
                outline: '2px solid #4f46e5',
                outlineOffset: 3,
                boxShadow: '0 0 0 1px rgba(15,23,42,0.1)',
              },
            }}
          >
            Continue to PlotWyze
          </Button>

          {/* Secondary links */}
          <Box
            component="div"
            sx={{
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
                color: colors.text.secondary,
              }}
            >
              Learn more about the developer
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
              }}
            >
              <Button
                type="button"
                variant="outlined"
                startIcon={<LanguageIcon />}
                href="https://www.koszuta.dev"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontSize: 14,
                  borderRadius: 999,
                  borderColor: colors.border,
                  color: colors.text.secondary,
                  '&:hover': {
                    borderColor: '#667eea',
                    color: isDark ? '#e5e7ff' : '#4338ca',
                    backgroundColor: isDark
                      ? 'rgba(102, 126, 234, 0.08)'
                      : 'rgba(102, 126, 234, 0.04)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #667eea',
                    outlineOffset: 2,
                  },
                }}
              >
                Portfolio site
              </Button>

              <Button
                type="button"
                variant="outlined"
                startIcon={<GitHubIcon />}
                href="https://github.com/AlexKosz"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontSize: 14,
                  borderRadius: 999,
                  borderColor: colors.border,
                  color: colors.text.secondary,
                  '&:hover': {
                    borderColor: '#667eea',
                    color: isDark ? '#e5e7ff' : '#4338ca',
                    backgroundColor: isDark
                      ? 'rgba(102, 126, 234, 0.08)'
                      : 'rgba(102, 126, 234, 0.04)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #667eea',
                    outlineOffset: 2,
                  },
                }}
              >
                GitHub
              </Button>

              <Button
                type="button"
                variant="outlined"
                startIcon={<LinkedInIcon />}
                href="https://www.linkedin.com/in/alex-koszuta-z43/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontSize: 14,
                  borderRadius: 999,
                  borderColor: colors.border,
                  color: colors.text.secondary,
                  '&:hover': {
                    borderColor: '#667eea',
                    color: isDark ? '#e5e7ff' : '#4338ca',
                    backgroundColor: isDark
                      ? 'rgba(102, 126, 234, 0.08)'
                      : 'rgba(102, 126, 234, 0.04)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #667eea',
                    outlineOffset: 2,
                  },
                }}
              >
                LinkedIn
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </ReusableModal>
  );
};

PortfolioNoticeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PortfolioNoticeModal;
